import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import {
  getLocalNotifications, getLocalUnreadCount, markLocalRead,
  markAllLocalRead, type LocalNotification,
} from '@/utils/localNotifications';
import {
  Badge,
  Box,
  Chip,
  ClickAwayListener,
  Divider,
  IconButton,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  DoneAll as DoneAllIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  getUnreadCount,
  listNotifications,
  markAllRead,
  markRead,
  type AppNotification,
} from '@/api/notifications';
import { ordersApi } from '@/api/orders';
import { tenantApi } from '@/api/tenant';
import { brand } from '@/theme/theme';
import { useSubscriptionGate } from '@/hooks/useSubscriptionGate';

const POLL_INTERVAL_MS = 30_000;

function typeColor(type: string): string {
  if (type === 'NEW_ORDER') return '#1976d2';
  if (type === 'ORDER_CONFIRMED') return '#388e3c';
  if (type === 'ORDER_DELIVERED') return '#6a1b9a';
  if (type === 'ORDER_CANCELLED') return '#757575';
  if (type === 'ORDER_REJECTED') return '#c62828';
  if (type === 'MONTHLY_BILL') return '#e65100';
  if (type === 'REP_REQUEST') return '#7B61FF';
  if (type === 'REP_REQUEST_ACCEPTED') return '#388e3c';
  if (type === 'REP_REQUEST_REJECTED') return '#c62828';
  if (type === 'REP_REQUEST_REMOVED') return '#757575';
  if (type === 'REP_BLOCKED') return '#b71c1c';
  if (type === 'REP_UNBLOCKED') return '#2e7d32';
  return brand.violet;
}

/** Unified notification shape used for rendering (backend + local). */
interface UnifiedNotification {
  uid: string;          // unique across both sources
  type: string;
  title: string;
  message: string;
  senderTenantName?: string;
  isRead: boolean;
  link?: string | null;
  createdAt: string;
  source: 'backend' | 'local';
  backendId?: number;
  localId?: string;
  referenceId?: number;
  orderAmount?: number;
  itemsCount?: number;
  partyArea?: string;
  partyCity?: string;
  partyGstin?: string;
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function formatAbsoluteTime(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

const REP_NOTIFICATION_TYPES = new Set([
  'REP_REQUEST', 'REP_REQUEST_ACCEPTED', 'REP_REQUEST_REJECTED',
  'REP_REQUEST_REMOVED', 'REP_BLOCKED', 'REP_UNBLOCKED',
]);

const ORDER_NOTIF_TYPES = new Set(['NEW_ORDER', 'ORDER_CONFIRMED', 'ORDER_DELIVERED', 'ORDER_CANCELLED', 'ORDER_REJECTED']);

function notificationPath(n: AppNotification): string | null {
  if (n.type === 'NEW_ORDER') return n.referenceId ? `/orders/inbox/${n.referenceId}` : null;
  if (REP_NOTIFICATION_TYPES.has(n.type)) return '/salesreps';
  if (!n.referenceId) return null;
  return `/orders/my/${n.referenceId}`;
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [unified, setUnified] = useState<UnifiedNotification[]>([]);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const subGate = useSubscriptionGate();

  function mergeNotifications(backend: AppNotification[], local: LocalNotification[]): UnifiedNotification[] {
    const b: UnifiedNotification[] = backend.map(n => ({
      uid: `b-${n.id}`, type: n.type, title: n.title, message: n.message,
      senderTenantName: n.senderTenantName, isRead: n.isRead,
      link: notificationPath(n), createdAt: n.createdAt,
      source: 'backend' as const, backendId: n.id, referenceId: n.referenceId,
      orderAmount: n.orderAmount, itemsCount: n.itemsCount,
      partyArea: n.partyArea, partyCity: n.partyCity, partyGstin: n.partyGstin,
    }));
    const l: UnifiedNotification[] = local.map(n => ({
      uid: `l-${n.id}`, type: n.type, title: n.title, message: n.message,
      senderTenantName: n.senderTenantName, isRead: n.isRead,
      link: n.link ?? null, createdAt: n.createdAt,
      source: 'local' as const, localId: n.id,
    }));
    return [...b, ...l].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  const refreshCount = useCallback(() => {
    const localCount = user ? getLocalUnreadCount(user.tenantId) : 0;
    getUnreadCount().then(c => setCount(c + localCount)).catch(() => setCount(localCount));
  }, [user]);

  const fetchNotifications = useCallback(async () => {
    const local = user ? getLocalNotifications(user.tenantId) : [];
    try {
      const page = await listNotifications({ size: 30 });
      const merged = mergeNotifications(page.content, local);
      setUnified(merged);
      setCount(page.content.filter(n => !n.isRead).length + local.filter(n => !n.isRead).length);

      // Enrich order notifications with amount, item count, city and GST
      const toEnrich = merged.filter(n =>
        ORDER_NOTIF_TYPES.has(n.type) && n.referenceId && n.orderAmount == null,
      );
      if (toEnrich.length > 0) {
        const [orderResults, peersResult] = await Promise.allSettled([
          Promise.allSettled(toEnrich.map(n => ordersApi.get(n.referenceId!))),
          tenantApi.listPeers(undefined, 0, 500),
        ]);
        const peers = peersResult.status === 'fulfilled' ? peersResult.value.content : [];
        const enrichMap = new Map<string, Partial<UnifiedNotification>>();
        if (orderResults.status === 'fulfilled') {
          orderResults.value.forEach((r, idx) => {
            if (r.status !== 'fulfilled') return;
            const order = r.value;
            const n = toEnrich[idx];
            const extra: Partial<UnifiedNotification> = {
              orderAmount: (order.totalAmount ?? 0) - (order.totalTax ?? 0),
              itemsCount: order.items?.length ?? 0,
            };
            if (n.senderTenantName && peers.length > 0) {
              const peer = peers.find(p => p.name === n.senderTenantName);
              if (peer) {
                extra.partyArea = peer.area;
                extra.partyCity = peer.city;
                extra.partyGstin = peer.gstin;
              }
            }
            enrichMap.set(n.uid, extra);
          });
        }
        if (enrichMap.size > 0) {
          setUnified(prev => prev.map(n => {
            const extra = enrichMap.get(n.uid);
            return extra ? { ...n, ...extra } : n;
          }));
        }
      }
    } catch {
      setUnified(mergeNotifications([], local));
      setCount(local.filter(n => !n.isRead).length);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refreshCount]);

  const handleOpen = () => {
    setOpen((v) => !v);
    if (!open) fetchNotifications();
  };

  const handleMarkRead = async (n: UnifiedNotification) => {
    if (n.source === 'backend' && n.backendId) {
      await markRead(n.backendId);
    } else if (n.source === 'local' && n.localId) {
      markLocalRead(n.localId);
    }
    setUnified(prev => prev.map(x => x.uid === n.uid ? { ...x, isRead: true } : x));
    setCount(c => Math.max(0, c - 1));
  };

  const handleMarkAll = async () => {
    if (user) markAllLocalRead(user.tenantId);
    await markAllRead().catch(() => {});
    setUnified(prev => prev.map(n => ({ ...n, isRead: true })));
    setCount(0);
  };

  const handleNotificationClick = async (n: UnifiedNotification) => {
    if (!n.isRead) await handleMarkRead(n);
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  const visible = subGate.isBlocked
    ? unified.filter(n => n.type !== 'NEW_ORDER')
    : unified;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box>
        <IconButton
          ref={anchorRef}
          onClick={handleOpen}
          aria-label="notifications"
          sx={{ color: brand.navy }}
        >
          <Badge
            badgeContent={count}
            color="error"
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: 10,
                fontWeight: 700,
                minWidth: 18,
                height: 18,
                padding: '0 4px',
              },
            }}
          >
            <NotificationsIcon sx={{ fontSize: 22 }} />
          </Badge>
        </IconButton>

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-end"
          style={{ zIndex: 1300 }}
        >
          <Paper
            elevation={8}
            sx={{
              width: 360,
              maxHeight: 500,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                px: 2,
                py: 1.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: 13, color: brand.navy }}>
                Notifications
              </Typography>
              {count > 0 && (
                <IconButton size="small" onClick={handleMarkAll} title="Mark all as read">
                  <DoneAllIcon fontSize="small" sx={{ color: brand.violet }} />
                </IconButton>
              )}
            </Box>

            {/* List */}
            <Box sx={{ overflowY: 'auto', flex: 1 }}>
              {visible.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No notifications
                  </Typography>
                </Box>
              ) : (
                visible.map((n, i) => (
                  <Box key={n.uid}>
                    <Box
                      onClick={() => handleNotificationClick(n)}
                      sx={{
                        px: 2, py: 1.25,
                        cursor: n.link ? 'pointer' : (n.isRead ? 'default' : 'pointer'),
                        bgcolor: n.isRead ? 'transparent' : 'action.hover',
                        '&:hover': { bgcolor: 'action.selected' },
                      }}
                    >
                      {/* Type chip + unread dot */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip
                          label={n.type.replace(/_/g, ' ')}
                          size="small"
                          sx={{ fontSize: 9, height: 18, bgcolor: typeColor(n.type), color: '#fff', fontWeight: 700 }}
                        />
                        {!n.isRead && (
                          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: brand.violet, flexShrink: 0 }} />
                        )}
                      </Box>

                      <Typography sx={{ fontSize: 12, fontWeight: n.isRead ? 400 : 600, color: brand.navy }}>
                        {n.title}
                      </Typography>
                      {ORDER_NOTIF_TYPES.has(n.type) ? (
                        <Box
                          sx={{
                            mt: 0.75,
                            mb: 0.5,
                            p: 1,
                            borderRadius: 1,
                            bgcolor: '#f4f6ff',
                            borderLeft: `3px solid ${typeColor(n.type)}`,
                          }}
                        >
                          {n.senderTenantName && (
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: brand.navy, lineHeight: 1.4 }}>
                              🏢 {n.senderTenantName}{[n.partyArea, n.partyCity].filter(Boolean).join(', ') ? `, ${[n.partyArea, n.partyCity].filter(Boolean).join(', ')}` : ''}
                            </Typography>
                          )}
                          {n.partyGstin && (
                            <Typography sx={{ fontSize: 10, color: 'text.secondary', mt: 0.25 }}>
                              GST: {n.partyGstin}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
                            {n.orderAmount != null && n.orderAmount > 0 && (
                              <Box sx={{ bgcolor: '#e8f5e9', borderRadius: 6, px: 1, py: 0.25 }}>
                                <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#2e7d32' }}>
                                  ₹{Number(n.orderAmount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                </Typography>
                              </Box>
                            )}
                            {n.itemsCount != null && n.itemsCount > 0 && (
                              <Box sx={{ bgcolor: '#e3f2fd', borderRadius: 6, px: 1, py: 0.25 }}>
                                <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#1565c0' }}>
                                  {n.itemsCount} item{n.itemsCount !== 1 ? 's' : ''}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      ) : (
                        <>
                          {n.senderTenantName && (
                            <Typography sx={{ fontSize: 11, color: brand.violet, fontWeight: 600, mt: 0.25 }}>
                              From: {n.senderTenantName}
                            </Typography>
                          )}
                          <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
                            {n.message}
                          </Typography>
                        </>
                      )}
                      <Typography sx={{ fontSize: 10, color: 'text.disabled', mt: 0.5 }}>
                        {formatAbsoluteTime(n.createdAt)} · {timeAgo(n.createdAt)}
                      </Typography>
                    </Box>
                    {i < visible.length - 1 && <Divider />}
                  </Box>
                ))
              )}
            </Box>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
